// BankAuction.js
import React, { useContext, useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  Stack,
  IconButton,
  Autocomplete
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FormsBottomNavbar from './FormsBottomNavbar';
import SearchBar from './PostBack';
import { AuthContext } from '../../AuthContext/AuthContext';
import { GOOGLEMAPS_API_KEY } from '../../Api/ApiUrls';

const FilterContainer = styled(Paper)(({ theme }) => ({
  padding: '16px',
  margin: '8px',
  borderRadius: '12px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  backgroundColor: '#fff',
  minHeight: "75vh",
  marginBottom: "80px"
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 'bold',
  fontSize: '16px',
  marginBottom: '12px',
  color: '#333'
}));

const StyledButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#8B4513',
  color: 'white',
  fontWeight: 'bold',
  borderRadius: '8px',
  padding: '8px 16px',
  '&:hover': {
    backgroundColor: '#654321',
  },
}));

const FileItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '8px 12px',
  margin: '4px 0',
  backgroundColor: '#f8f9fa',
  borderRadius: '6px',
  border: '1px solid #e9ecef',
}));

const FileInfo = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 1,
}));

const BankAuction = () => {
  const { userId } = useContext(AuthContext);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    auctionId: '',
    bankName: '',
    propertyType: '',
    actionType: '',
    location: '',
    cityTown: '',
    areaTown: '',
    area: '',
    possession: '',
    reservePrice: '',
    possessionStatus: '',
    emdAmount: '',
    bidIncrement: '',
    emdSubmission: '',
    auctionStartDate: '',
    auctionEndDate: '',
    bankContact: '',
    downloadAuctionFile: '', // This should be a short string (file path/name)
    description: '' // Added description field
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [isGoogleLoaded, setIsGoogleLoaded] = useState(false);
  const autocompleteServiceRef = useRef(null);

  // Google Maps Script for location autocomplete - Improved version
  useEffect(() => {
    const initGoogleMaps = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        setIsGoogleLoaded(true);
        autocompleteServiceRef.current = new window.google.maps.places.AutocompleteService();
        return true;
      }
      return false;
    };

    // Check if Google Maps is already loaded
    if (initGoogleMaps()) {
      return;
    }

    // If not loaded, add the script
    if (!window.google) {
      const script = document.createElement("script");
      // Replace YOUR_API_KEY with your actual Google Maps API key
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLEMAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        if (initGoogleMaps()) {
          console.log("✅ Google Maps API loaded successfully");
        }
      };
      
      script.onerror = () => {
        console.error("❌ Failed to load Google Maps API");
      };
      
      document.head.appendChild(script);
    }

    // Cleanup function
    return () => {
      autocompleteServiceRef.current = null;
    };
  }, []);

  const handleFormDataChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Fetch Places with predictions for location autocomplete
  const fetchPlaces = (input) => {
    if (!input || input.length < 3 || !isGoogleLoaded || !autocompleteServiceRef.current) {
      setLocationOptions([]);
      return;
    }

    autocompleteServiceRef.current.getPlacePredictions(
      { 
        input, 
        types: ["geocode", "establishment"], 
        componentRestrictions: { country: "in" } 
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setLocationOptions(predictions.map((p) => p.description));
        } else {
          setLocationOptions([]);
          console.log("No predictions found or error:", status);
        }
      }
    );
  };

  // Debounced fetchPlaces function to avoid too many API calls
  const debouncedFetchPlaces = useRef(null);

  useEffect(() => {
    debouncedFetchPlaces.current = debounce(fetchPlaces, 300);
  }, [isGoogleLoaded]);

  // Debounce utility function
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
   // Fetch geocoding for a locationfetchPlaces 
  const getGeocode = async (locationName) => {
    return new Promise((resolve) => {
      if (!window.google || !window.google.maps) {
        resolve({ location: locationName, lat: "", long: "" });
        return;
      }

      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: locationName }, (results, status) => {
        if (status === 'OK' && results[0]) {
          const lat = results[0].geometry.location.lat().toFixed(4);
          const lng = results[0].geometry.location.lng().toFixed(4);
          resolve({ location: locationName, lat, long: lng });
        } else {
          resolve({ location: locationName, lat: "", long: "" });
        }
      });
    });
  };

const handleSave = async () => {
  try {
      // Get geocoding for location
      let lat = "";
      let long = "";
      
      if (formData.location) {
        const geocodeResult = await getGeocode(formData.location);
        lat = geocodeResult.lat;
        long = geocodeResult.long;
      }

    const formPayload = new FormData();

    formPayload.append("user_id", userId);
    formPayload.append("bank_name", formData.bankName);
    formPayload.append("property_type", formData.propertyType);
    formPayload.append("action_type", formData.actionType);
    formPayload.append("location", formData.location);
    formPayload.append("city_town", formData.cityTown);
    formPayload.append("area_town", formData.areaTown);
    formPayload.append("lat", lat);
    formPayload.append("long", long);
    formPayload.append("area", formData.area);
    formPayload.append("units", "");
    formPayload.append("possession", formData.possession);
    formPayload.append("reserve_price", formData.reservePrice);
    formPayload.append("possession_status", formData.possessionStatus);
    formPayload.append("emd_amount", formData.emdAmount);
    formPayload.append("bid_increment", formData.bidIncrement);
    formPayload.append("emd_submission", formData.emdSubmission);
    formPayload.append("auction_start_datetime", formData.auctionStartDate);
    formPayload.append("auction_end_datetime", formData.auctionEndDate);
    formPayload.append("bank_contact_details", formData.bankContact);
    formPayload.append("description", formData.description);
    formPayload.append("status", "Rejected");

    // 🔥 FILES
    selectedFiles.forEach((file) => {
      formPayload.append("new_documents", file);
    });

    const response = await fetch(
      "https://landnest.net:81/auction-property/",
      {
        method: "POST",
        body: formPayload, // 🚨 NO JSON HEADER
      }
    );

    if (!response.ok) {
      const err = await response.text();
      throw new Error(err);
    }

    alert("Auction saved successfully");
    navigate(-1);
  } catch (err) {
    alert("Error: " + err.message);
  }
};


  // Separate function to handle file uploads
  const uploadFiles = async (bankpropId, files) => {
    try {
      const formData = new FormData();
      formData.append('bankprop_id', bankpropId);
      
      files.forEach((file, index) => {
        formData.append('auction_files', file);
      });

      const uploadResponse = await fetch("https://landnest.net:81/upload-auction-files/", {
        method: "POST",
        body: formData,
      });

      if (uploadResponse.ok) {
        console.log("✅ Files uploaded successfully");
      } else {
        console.log("❌ File upload failed");
      }
    } catch (error) {
      console.log("🚨 Error uploading files:", error);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const handleBackClick = () => navigate(-1);

  // Handle file selection - store file names in downloadAuctionFile field
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files);
    
    if (files.length > 0) {
      // For download_auction_file field: use first file name or generate a short identifier
      const firstFileName = files[0].name;
      const shortFileName = firstFileName.length > 50 
        ? firstFileName.substring(0, 47) + '...' 
        : firstFileName;
      
      // Store the short file name in download_auction_file field
      handleFormDataChange('downloadAuctionFile', shortFileName);
      
      // Store actual files for separate upload
      setSelectedFiles(prev => [...prev, ...files]);
    }
    
    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  };

  // Handle file deletion
  const handleFileDelete = (indexToDelete) => {
    setSelectedFiles(prev => prev.filter((_, index) => index !== indexToDelete));
    
    // If we're deleting the first file, update the downloadAuctionFile field
    if (indexToDelete === 0 && selectedFiles.length > 0) {
      if (selectedFiles.length === 1) {
        // No files left, clear the field
        handleFormDataChange('downloadAuctionFile', '');
      } else {
        // Update with the new first file name
        const newFirstFileName = selectedFiles[1].name;
        const shortFileName = newFirstFileName.length > 50 
          ? newFirstFileName.substring(0, 47) + '...' 
          : newFirstFileName;
        handleFormDataChange('downloadAuctionFile', shortFileName);
      }
    }
  };

  // Clear all files
  const handleClearAllFiles = () => {
    setSelectedFiles([]);
    handleFormDataChange('downloadAuctionFile', '');
  };

  // Options for dropdowns
  const bankOptions = [
    'State Bank of India',
    'HDFC Bank',
    'ICICI Bank',
    'Axis Bank',
    'Punjab National Bank',
    'Bank of Baroda',
    'Canara Bank',
    'Union Bank of India'
  ];

  const propertyTypeOptions = [
    'Plots',
    'Residential Building',
    'Commercial Building',
    'Land',
    'Flat',
    'House',
    'Villa'
  ];

  const possessionOptions = [
    'Physical',
    'Symbolic',
    'Physical/Symbolic'
  ];

  const cityOptions = [
    'Bangalore',
    'Hyderabad',
    'Delhi',
    'Mumbai',
    'Chennai',
    'Pune',
    'Kolkata'
  ];

  return (
    <>
      <SearchBar onBackClick={handleBackClick} />

      <Box sx={{ 
        padding: '16px', 
        backgroundColor: '#f5f1eb',
        minHeight: '100vh',
        height: '100%',
        marginTop: "15%",
        paddingBottom: "80px"
      }}>
        <FilterContainer>
          {/* Basic Information */}
          <SectionTitle>Basic Information</SectionTitle>
         

         <FormControl fullWidth sx={{ mb: 2 }} size="small">
  <TextField
    label="Bank Name"
    value={formData.bankName}
    onChange={(e) => handleFormDataChange('bankName', e.target.value)}
    variant="outlined"
    size="small"
  />
</FormControl>


          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Property Type</InputLabel>
            <Select
              value={formData.propertyType}
              label="Property Type"
              onChange={(e) => handleFormDataChange('propertyType', e.target.value)}
            >
              {propertyTypeOptions.map((type) => (
                <MenuItem key={type} value={type}>{type}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Action Type"
            placeholder="Enter action type"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.actionType}
            onChange={(e) => handleFormDataChange('actionType', e.target.value)}
          />

          {/* Location Details */}
          <SectionTitle>Location Details</SectionTitle>
          
          {/* Location with autocomplete - FIXED VERSION */}
          <Autocomplete 
            freeSolo
            disableClearable
            options={locationOptions}
            value={formData.location}
            onChange={(event, newValue) => {
              handleFormDataChange('location', newValue || '');
            }}
            onInputChange={(event, newInputValue) => {
              handleFormDataChange('location', newInputValue);
              if (debouncedFetchPlaces.current) {
                debouncedFetchPlaces.current(newInputValue);
              }
            }}
            filterOptions={(x) => x}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label="Location" 
                placeholder="Type to search locations..." 
                size="small"
                sx={{ mb: 2 }}
                InputProps={{
                  ...params.InputProps,
                  type: 'search',
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option}>
                {option}
              </li>
            )}
            noOptionsText={isGoogleLoaded ? "No locations found" : "Loading Google Maps..."}
          />

           <TextField
            fullWidth
            label="City/Town"
            placeholder="e.g. Bangalore"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.cityTown}
            onChange={(e) => handleFormDataChange('cityTown', e.target.value)}
          />

          <TextField
            fullWidth
            label="Area/Town"
            placeholder="e.g., Yeswanthpura"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.areaTown}
            onChange={(e) => handleFormDataChange('areaTown', e.target.value)}
          />

          <TextField
            fullWidth
            label="Area"
            placeholder="e.g., 900 sft"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.area}
            onChange={(e) => handleFormDataChange('area', e.target.value)}
          />

          {/* Possession Details */}
          <SectionTitle>Possession Details</SectionTitle>
         

          <FormControl fullWidth sx={{ mb: 2 }} size="small">
            <InputLabel>Possession Status</InputLabel>
            <Select
              value={formData.possessionStatus}
              label="Possession Status"
              onChange={(e) => handleFormDataChange('possessionStatus', e.target.value)}
            >
              <MenuItem value="Physical">Physical</MenuItem>
              <MenuItem value="Symbolic">Symbolic</MenuItem>
              <MenuItem value="Symbolic/Physical">Symbolic/Physical</MenuItem>
            </Select>
          </FormControl>

          {/* Auction Details */}
          <SectionTitle>Auction Details</SectionTitle>
          <TextField
            fullWidth
            label="Reserve Price"
            placeholder="e.g., 31,32,000"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.reservePrice}
            onChange={(e) => handleFormDataChange('reservePrice', e.target.value)}
          />

          <TextField
            fullWidth
            label="EMD Amount"
            placeholder="e.g., 3,13,200"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.emdAmount}
            onChange={(e) => handleFormDataChange('emdAmount', e.target.value)}
          />

          <TextField
            fullWidth
            label="Bid Increment"
            placeholder="e.g., 10,000"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            value={formData.bidIncrement}
            onChange={(e) => handleFormDataChange('bidIncrement', e.target.value)}
          />

          <TextField
            fullWidth
            label="EMD Submission Date & Time"
            type="datetime-local"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={formData.emdSubmission}
            onChange={(e) => handleFormDataChange('emdSubmission', e.target.value)}
          />

          <TextField
            fullWidth
            label="Auction Start Date & Time"
            type="datetime-local"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={formData.auctionStartDate}
            onChange={(e) => handleFormDataChange('auctionStartDate', e.target.value)}
          />

          <TextField
            fullWidth
            label="Auction End Date & Time"
            type="datetime-local"
            variant="outlined"
            size="small"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={formData.auctionEndDate}
            onChange={(e) => handleFormDataChange('auctionEndDate', e.target.value)}
          />

          {/* Contact & Files */}
          <SectionTitle>Contact & Documents</SectionTitle>
         <TextField
  fullWidth
  label="Bank Contact Details"
  placeholder="e.g., 9876543210"
  variant="outlined"
  size="small"
  sx={{ mb: 2 }}
  value={formData.bankContact}
  onChange={(e) => {
    // Allow only digits
    const value = e.target.value.replace(/\D/g, "");
    handleFormDataChange("bankContact", value);
  }}
  type="tel"
  inputProps={{
    inputMode: "numeric",
    pattern: "[0-9]*",
    maxLength: 10, // optional
  }}
/>


          {/* Description Field */}
          <SectionTitle>Description</SectionTitle>
          <TextField
            fullWidth
            label="Description"
            placeholder="Enter property description..."
            variant="outlined"
            size="small"
            multiline
            rows={3}
            sx={{ mb: 2 }}
            value={formData.description}
            onChange={(e) => handleFormDataChange('description', e.target.value)}
          />

          {/* Auction Files */}
          <SectionTitle>Auction Files</SectionTitle>
          <Box sx={{ mb: 2 }}>
            <label
              htmlFor="auction-files"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                padding: "12px 16px",
                border: "2px dashed #8B4513",
                borderRadius: "8px",
                background: "#fffaf0",
                cursor: "pointer",
                marginBottom: "12px",
                transition: "all 0.3s ease"
              }}
              onMouseEnter={(e) => {
                e.target.style.background = "#f5f1eb";
                e.target.style.borderColor = "#654321";
              }}
              onMouseLeave={(e) => {
                e.target.style.background = "#fffaf0";
                e.target.style.borderColor = "#8B4513";
              }}
            >
              <AttachFileIcon sx={{ color: '#8B4513' }} />
              <Typography sx={{ color: '#8B4513', fontWeight: '500' }}>
                Click to Select Files
              </Typography>
            </label>

            <input
              id="auction-files"
              type="file"
              multiple
              style={{ display: "none" }}
              onChange={handleFileSelect}
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx" // Accept images and documents
            />

            {/* SHOW SELECTED FILES WITH DELETE BUTTONS */}
            {selectedFiles.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="body2" sx={{ color: "#555", fontWeight: '500' }}>
                    Selected Files ({selectedFiles.length})
                  </Typography>
                  <Button 
                    size="small" 
                    onClick={handleClearAllFiles}
                    sx={{ color: '#d32f2f', fontSize: '12px' }}
                  >
                    Clear All
                  </Button>
                </Box>
                
                {selectedFiles.map((file, index) => (
                  <FileItem key={index}>
                    <FileInfo>
                      <AttachFileIcon sx={{ color: '#8B4513', fontSize: '18px' }} />
                      <Typography sx={{ fontSize: "14px", color: "#333", flex: 1 }}>
                        {file.name}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: "#666" }}>
                        {(file.size / 1024).toFixed(2)} KB
                      </Typography>
                    </FileInfo>
                    <IconButton 
                      size="small" 
                      onClick={() => handleFileDelete(index)}
                      sx={{ color: '#d32f2f', '&:hover': { backgroundColor: 'rgba(211, 47, 47, 0.04)' } }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </FileItem>
                ))}
                
                {formData.downloadAuctionFile && (
                  <Typography variant="caption" sx={{ color: "#666", mt: 1, display: 'block' }}>
                    Primary file reference: {formData.downloadAuctionFile}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1} sx={{ mt: 3, mb: 2 }}>
            <Button 
              variant="outlined" 
              fullWidth
              onClick={handleCancel}
              sx={{ 
                borderRadius: '8px',
                borderColor: '#8B4513',
                color: '#8B4513',
                '&:hover': {
                  borderColor: '#654321',
                  backgroundColor: 'rgba(139, 69, 19, 0.04)'
                }
              }}
            >
              Cancel
            </Button>
            <StyledButton fullWidth onClick={handleSave}>
              Save
            </StyledButton>
          </Stack>
        </FilterContainer>
      </Box>
      <FormsBottomNavbar />
    </>
  );
};

export default BankAuction;